/**
 * The value produced by the document-request widget (WPS-130).
 *
 * Lives apart from the component so both the widget and any caller that needs
 * to read or seed the value can import it without tripping Fast Refresh's
 * "components only" rule.
 *
 * Serialised to a JSON string when stored, because the platform has no JSON
 * attribute type - the carrier is a LONG_TEXT claim attribute whose
 * ``configuration_data.widget`` selects this widget.
 */
export interface CustomRequestItem {
  label: string
  description: string
  /**
   * Stable client-side row identity, used as the React key.
   *
   * Index keys break on removal: deleting a row shifts every later index, so
   * React reuses the wrong DOM nodes and focus lands in the wrong input. The id
   * rides along in the stored JSON so it survives the parse round-trip; the
   * backend reads only ``label`` and ``description`` and ignores it.
   */
  id: string
}

export interface DocumentRequestValue {
  requirement_keys: string[]
  custom_items: CustomRequestItem[]
  note: string
}

let fallbackCounter = 0

/** A stable id for a newly added custom row. */
export function newCustomItemId(): string {
  const globalCrypto = globalThis.crypto
  if (globalCrypto && typeof globalCrypto.randomUUID === 'function') {
    return globalCrypto.randomUUID()
  }
  fallbackCounter += 1
  return `custom-row-${fallbackCounter}`
}

export const EMPTY_DOCUMENT_REQUEST: DocumentRequestValue = {
  requirement_keys: [],
  custom_items: [],
  note: '',
}

export function parseDocumentRequest(raw: unknown): DocumentRequestValue {
  if (!raw) return EMPTY_DOCUMENT_REQUEST
  let value: unknown = raw
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw)
    } catch {
      // Free text typed straight into the field reads most naturally as a note.
      return { ...EMPTY_DOCUMENT_REQUEST, note: raw }
    }
  }
  if (typeof value !== 'object' || value === null) return EMPTY_DOCUMENT_REQUEST
  const v = value as Partial<DocumentRequestValue>
  return {
    requirement_keys: Array.isArray(v.requirement_keys)
      ? v.requirement_keys.map(String)
      : [],
    // Blank rows are KEPT: this parses the widget's own editing state, and an
    // "Add another item" row starts empty. Dropping unlabelled rows here made
    // the button look broken - the new row was stripped on the round-trip
    // before it could ever be typed into. Blank labels are discarded at the
    // point they stop being editable: the backend's
    // ``synthesize_custom_requirements`` ignores them.
    custom_items: Array.isArray(v.custom_items)
      ? v.custom_items.map((i) => ({
          label: String(i?.label ?? ''),
          description: String(i?.description ?? ''),
          // Preserved so the row keeps its React key across the round-trip; a
          // row authored before ids existed gets one now.
          id: i?.id ? String(i.id) : newCustomItemId(),
        }))
      : [],
    note: typeof v.note === 'string' ? v.note : '',
  }
}
